import React from "react"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config"

import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { AmplitudeHelper } from "../../helpers/amplitude.helper"
import { useAppSelector } from "../../store/selectors/app.selector"
import { userDetailsSelector } from "../../store/selectors/userDetails.selector"
import type {
    IErrorBoundaryState,
    IUserDetailsState,
} from "../../types/state.types"

class ErrorBoundaryClass extends React.Component<
    IErrorBoundaryProps,
    IErrorBoundaryState
> {
    constructor(props: IErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error: Error): IErrorBoundaryState {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log error to console
        console.error(error)

        // If user details are not loaded, don't log the error
        if (!this.props.userDetails?.profile) {
            return
        }

        // Log error to Amplitude
        AmplitudeHelper.logEvent(AmplitudeEvent.ENCOUNTERED_ERROR_FE, {
            errorCode: error.name || "Unknown Error",
            errorMessage: error.message,
            errorInfo: errorInfo,
            practiceId:
                this.props.userDetails.selectedPracticeRole?.practice?.id,
            practiceName:
                this.props.userDetails.selectedPracticeRole?.practice?.name,
            email: this.props.userDetails.profile.email,
        })
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children
        }

        return (
            <div className="primary-container p-[32px]">
                <PrimaryText typography="h1" centered>
                    Oops, something went wrong
                </PrimaryText>

                <PrimaryText typography="text" centered className="mt-[24px]">
                    We've encountered an error and our team has been notified.
                    Please try refreshing the page.
                </PrimaryText>

                <div className="mt-[32px] flex justify-center">
                    <ButtonElement
                        label="Refresh page"
                        type="primary"
                        htmlType="button"
                        size="large"
                        onClick={() => {
                            window.location.reload()
                        }}
                    />
                </div>
            </div>
        )
    }
}

export const ErrorBoundary: React.FC<IErrorBoundaryProps> = props => {
    const userDetails = useAppSelector(userDetailsSelector)

    return <ErrorBoundaryClass {...props} userDetails={userDetails} />
}

interface IErrorBoundaryProps {
    children: React.ReactNode
    userDetails: IUserDetailsState
}
